import { Box, Button, Dialog, DialogActions, DialogContent, Grid2, styled, Typography } from "@mui/material";
import CloseButton from "../../../../Components/CloseButton";
import { PrimaryFormLoadingActionButton } from "../../../../Features/Table/GenericDraggableTable/TableStyles";
import { useTranslation } from "react-i18next";
import SingleDatePicker from "../../../../Helper/DatePicker/SingleDatePicker";
import SingleTimePicker from "../../../../Helper/DatePicker/SingleTimePicker";
import { useEffect, useState } from "react";
import { downloadIotStateData } from "../../../../Api/IoTApi";
import { useBatteryInfo, useVehicleInfo } from "../../../../Pages/DigitalTwinV3/DigitalTwinContext";
import dayjs, { utc } from "dayjs";
import timezone from "dayjs/plugin/timezone";
import duration from "dayjs/plugin/duration";
import Cookies from "universal-cookie";
import { DEFAULT_DEVICE_HEALTH_DOWNLOAD_MAX_HOURS, getDownloadErrorMessage } from "../../../../Helper/Organization/DeviceHealthDownload";
import { resolveDeviceIdLabel, resolvePrimaryIdentifier } from "../../../../Helper/DigitalTwin/vehicleIdentifier";
import { useBaseEntityIdLabel, useGlobalConfig } from "../../../../Hooks/usePagesContent";
import { useToast } from "../../../../Hooks/useToast";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(duration);

const StyledDialog = styled(Dialog)(({ theme }) => ({
    "& .MuiPaper-root": {
      borderRadius: 8,
      boxShadow: theme.shadows[5],
      width: "455px",
      maxWidth: "90%",
    },
  }));

export default function DeviceHealthDownloadDataDialog({open, handleClose, maxHours = DEFAULT_DEVICE_HEALTH_DOWNLOAD_MAX_HOURS}) {

    const { batteryID, batteryInfo, showBusDeviceInfo } = useBatteryInfo()
    const vehicleInfo = useVehicleInfo()

    const {t} = useTranslation(['common', 'generic_table'])
    // Same labels as the Battery Vitals ID box (InstantaneousMatrix).
    const deviceIdLabel = resolveDeviceIdLabel(useGlobalConfig(), t('common:device_id'));
    const vehicleIdLabel = useBaseEntityIdLabel(t('digital_twin:section_ids.bus_number'));
    const { openNotification } = useToast();
    const maxHoursText = t('digital_twin:device_health_download.hours', { count: maxHours });
    const [selectedDate, setSelectedDate] = useState(null);
    const [selectedStartTime, setSelectedStartTime] = useState(null);
    const [selectedEndTime, setSelectedEndTime] = useState(null);
    const [showEmptyDateTimeError, setShowEmptyDateTimeError] = useState(false);
    const [showHourDifferenceError, setShowHourDifferenceError] = useState(false);
    const [showInvalidTimeError, setShowInvalidTimeError] = useState(false);
    const [showFutureTimeError, setShowFutureTimeError] = useState(false);

    const cookies = new Cookies();
    const timezone = cookies.get("timeZone") || "UTC";
    const localDate = dayjs().tz(timezone).format("YYYY-MM-DD");
    // The pickers return only a time of day, which is put on the selected date. "Now" and
    // the future-time cutoff only mean the current moment when that date is today.
    const isTodaySelected = selectedDate?.format("YYYY-MM-DD") === localDate;

    // Fills the dialog with the most recent allowed period: today, ending now. A period
    // cannot cross midnight, so it starts no earlier than 12:00 AM today.
    const selectLastPeriod = () => {
        const now = dayjs().tz(timezone).second(0).millisecond(0);
        const periodStart = now.subtract(Math.round(maxHours * 60), "minute");
        const startOfToday = now.startOf("day");
        setSelectedDate(dayjs(localDate));
        setSelectedStartTime(periodStart.isBefore(startOfToday) ? startOfToday : periodStart);
        setSelectedEndTime(now);
    }

    const onDateChange = (value) => {
        setSelectedDate(value);
    }

    const onStartTimeChange = (value) => {
        setSelectedStartTime(value);

    }
    const onEndTimeChange = (value) => {
        setSelectedEndTime(value);
    }

    const downloadCSVFile = (response) => {
        const blob = new Blob([response.data], { type: "text/csv" });
        const url = window.URL.createObjectURL(blob);

        // Try to extract filename from Content-Disposition
        const disposition = response.headers.get("Content-Disposition");
        let fileName = "data.csv";
        if (disposition && disposition.includes("filename=")) {
            fileName = disposition.split("filename=")[1].replace(/"/g, "").trim();
        }

        // Create a hidden download link and click it
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();

        // Free up memory
        window.URL.revokeObjectURL(url);
    }

    const hideAllErrors = () => {
        setShowHourDifferenceError(false);
        setShowEmptyDateTimeError(false);
        setShowInvalidTimeError(false);
        setShowFutureTimeError(false);
    }

    const checkDatesValidation = (checkForEmpty = true) => {
        hideAllErrors();

        if (checkForEmpty) {
            if (!selectedStartTime || !selectedEndTime || !selectedDate) {
                setShowEmptyDateTimeError(true);
                return false;
            }
        } else {
            if (!selectedStartTime || !selectedEndTime) {
                return false;
            }
        }

        setShowEmptyDateTimeError(false);

        const diffInMinutes = selectedEndTime.diff(selectedStartTime, "minute");

        if (diffInMinutes <= 0) {
            setShowInvalidTimeError(true);
            return false;
        }
        setShowInvalidTimeError(false);

        // A time picked before the date was switched to today can still be in the future.
        const now = dayjs().tz(timezone);
        const endMinuteOfDay = selectedEndTime.hour() * 60 + selectedEndTime.minute();
        if (isTodaySelected && endMinuteOfDay > now.hour() * 60 + now.minute()) {
            setShowFutureTimeError(true);
            return false;
        }

        if (diffInMinutes > maxHours * 60) {
            setShowHourDifferenceError(true);
            return false;
        }
        setShowHourDifferenceError(false);
        return true;
    }

    const prepareDatesForApi = () => {
        const dateTimeFormat = 'YYYY-MM-DD HH:mm:ss'

        const startDateTime = selectedDate
            .hour(selectedStartTime.hour())
            .minute(selectedStartTime.minute())
            .second(0)
            .millisecond(0)
            .format(dateTimeFormat);

        const endDateTime = selectedDate
            .hour(selectedEndTime.hour())
            .minute(selectedEndTime.minute())
            .second(0)
            .millisecond(0)
            .format(dateTimeFormat);

        return {startDateTime, endDateTime};
    }

    const handleDownload = async () => {
        try {
            const datesAreValid = checkDatesValidation();

            if (!datesAreValid || !batteryID) {
                return;
            }

            const {startDateTime, endDateTime} = prepareDatesForApi();

            const entityId = resolvePrimaryIdentifier({
                showVehicleIdentifier: showBusDeviceInfo?.() === true,
                vehicleIdLabel,
                deviceIdLabel,
                licensePlateNo: vehicleInfo?.licensePlateNo,
                deviceID: batteryInfo?.deviceID,
            });

            const response = await downloadIotStateData(batteryID, startDateTime, endDateTime, entityId);

            if (!response.data) {
                throw new Error("Failed to fetch file");
            }

            downloadCSVFile(response);
        } catch (err) {
            console.error("Download failed:", err);
            // A 400 (e.g. the range is longer than the backend allows) carries its reason.
            const message = await getDownloadErrorMessage(err);
            openNotification({
                text: message || t('digital_twin:device_health_download.download_failed'),
                icon: "toast-error",
            });
        }
    }

    useEffect(() => {
        hideAllErrors();
    }, [open]);

    useEffect(() => {
        checkDatesValidation(false);
    }, [selectedDate, selectedStartTime, selectedEndTime, maxHours]);

    return (
        <StyledDialog open={open} onClose={handleClose} sx={{ p: 3 }}>
            <DialogContent sx={{pb: 0}}>
                <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3}}>
                    <Typography variant="fw600s16c000000" sx={{lineHeight: '20px'}}>{t('digital_twin:device_health_download.title')}</Typography>
                    <CloseButton onClick={handleClose} />
                </Box>
                <Box sx={{mb: 2.5}}>
                    <Typography variant="fw400s14c000000" sx={{lineHeight: '24px'}}>{t('digital_twin:device_health_download.select_date')}</Typography>
                    <Box sx={{mt: 1, display: 'flex', alignItems: 'center', gap: 1}}>
                        <SingleDatePicker value={selectedDate} handleOnChange={onDateChange} maxDate={localDate} />
                        <Button variant="text" size="small" onClick={selectLastPeriod} sx={{textTransform: 'none'}}>
                            {t('digital_twin:device_health_download.last_period', { hours: maxHoursText })}
                        </Button>
                    </Box>
                </Box>
                <Box sx={{mb: 3}}>
                    <Typography variant="fw400s14c000000" sx={{lineHeight: '24px'}}>{t('digital_twin:device_health_download.define_period', { hours: maxHoursText })}</Typography>
                    <Grid2>
                        <Grid2 container gap={1} mt={1}>
                            <Box sx={{flex: 1}}>
                                <Typography variant="fw400s12c686868" sx={{lineHeight: '24px'}}>{t('digital_twin:device_health_download.start_time')}</Typography>
                                <SingleTimePicker value={selectedStartTime} handleOnChange={onStartTimeChange} showNow={false} disableFutureTimes={isTodaySelected} />
                            </Box>
                            <Box sx={{flex: 1}}>
                                <Typography variant="fw400s12c686868" sx={{lineHeight: '24px'}}>{t('digital_twin:device_health_download.end_time')}</Typography>
                                <SingleTimePicker value={selectedEndTime} handleOnChange={onEndTimeChange} isEndTime={true} showNow={isTodaySelected} disableFutureTimes={isTodaySelected} />
                            </Box>
                        </Grid2>
                        {showEmptyDateTimeError && <Box sx={{mt: 0.25}}>
                            <Typography variant="fw400s10c930500" sx={{lineHeight: '20px'}}>{t('digital_twin:device_health_download.error_empty')}</Typography>
                        </Box>}
                        {showHourDifferenceError && <Box sx={{mt: 0.25}}>
                            <Typography variant="fw400s10c930500" sx={{lineHeight: '20px'}}>{t('digital_twin:device_health_download.error_max_period', { hours: maxHoursText })}</Typography>
                        </Box>}
                        {showInvalidTimeError && <Box sx={{mt: 0.25}}>
                            <Typography variant="fw400s10c930500" sx={{lineHeight: '20px'}}>{t('digital_twin:device_health_download.error_invalid_time')}</Typography>
                        </Box>}
                        {showFutureTimeError && <Box sx={{mt: 0.25}}>
                            <Typography variant="fw400s10c930500" sx={{lineHeight: '20px'}}>{t('digital_twin:device_health_download.error_future_time')}</Typography>
                        </Box>}
                    </Grid2>
                </Box>
            </DialogContent>
            {/* Send Button */}
            <DialogActions sx={{ px: 3, pt: 0, pb: 3, justifyContent: "flex-end" }}>
            <PrimaryFormLoadingActionButton
                onClick={handleDownload}
            > {t('actions.download', 'Download')}
            </PrimaryFormLoadingActionButton>
            </DialogActions>
        </StyledDialog>
    )
}
